import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Check, Clock, Loader2 } from 'lucide-react';
import CabinCrewLayout from '../layouts/CabinCrewLayout';
import { AuthContext } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Şu an sadece İngilizce aktif
const LANGUAGES = {
  'english': { name: 'English', flag: '🇬🇧' }
};

const Exam = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { language } = useParams();
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(30 * 60);
  const [examStartTime] = useState(Date.now());
  
  const currentLang = LANGUAGES[language] || LANGUAGES['english'];
  const examLanguage = language || 'english';

  // Demo kullanıcıları bu sayfaya erişemez
  useEffect(() => {
    if (user?.is_demo) {
      toast.error('Bu özellik tam sürümde mevcuttur');
      navigate('/dashboard');
    }
  }, [user, navigate]);
  
  useEffect(() => {
    if (!user?.is_demo) {
      loadQuestions();
    }
  }, [language, user]);
  
  useEffect(() => {
    if (loading || submitting || generating) return;
    
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmit(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [loading, submitting, generating]);
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  const loadQuestions = async () => {
    setGenerating(true);
    try {
      // Try language-specific endpoint first, fallback to default
      const endpoint = examLanguage !== 'english' 
        ? `${API}/exam/questions/${examLanguage}`
        : `${API}/exam/questions`;
      
      const response = await axios.get(endpoint, { withCredentials: true });
      setQuestions(response.data);
      
      if (examLanguage !== 'english' && response.data.length === 0) {
        toast.info('Sorular hazırlanıyor, lütfen bekleyin...');
      }
    } catch (error) {
      if (error.response?.status === 403) {
        const detail = error.response?.data?.detail || '';
        if (detail.includes('days') || detail.includes('gün')) {
          toast.error(detail);
        } else if (detail.includes('profile') || detail.includes('Profile')) {
          toast.error('Önce profilinizi tamamlamanız gerekmektedir');
        } else {
          toast.error(detail || 'Sınava erişim reddedildi');
        }
      } else {
        toast.error('Sorular yüklenemedi. Lütfen tekrar deneyin.');
      }
      navigate('/dashboard');
    } finally {
      setLoading(false);
      setGenerating(false);
    }
  };
  
  const handleAnswer = (questionId, answerIndex) => {
    setAnswers({
      ...answers,
      [questionId]: answerIndex
    });
  };
  
  const handleSubmit = async (autoSubmit = false) => {
    if (!autoSubmit && Object.keys(answers).length < questions.length) {
      toast.error('Lütfen tüm soruları cevaplayın');
      return;
    }
    
    if (autoSubmit) {
      toast.warning('Süre doldu! Sınav otomatik olarak gönderiliyor...');
    }
    
    setSubmitting(true);
    
    try {
      const formattedAnswers = Object.entries(answers).map(([question_id, selected_answer]) => ({
        question_id,
        selected_answer
      }));
      
      // Use language-specific endpoint
      const endpoint = examLanguage !== 'english' 
        ? `${API}/exam/submit/${examLanguage}`
        : `${API}/exam/submit`;
      
      const response = await axios.post(
        endpoint,
        { answers: formattedAnswers },
        { withCredentials: true }
      );
      
      toast.success('Sınav tamamlandı!');
      navigate(`/results/${response.data.result_id}`);
    } catch (error) {
      toast.error('Sınav gönderilemedi');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading || generating) {
    return (
      <CabinCrewLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto"></div>
              <span className="absolute inset-0 flex items-center justify-center text-3xl">
                {currentLang.flag}
              </span>
            </div>
            <p className="mt-4 text-xl font-bold text-gray-700">
              {generating ? `${currentLang.name} soruları hazırlanıyor...` : 'Sorular yükleniyor...'}
            </p>
            {generating && (
              <p className="text-sm text-gray-500 mt-2">Bu işlem birkaç saniye sürebilir</p>
            )}
          </div>
        </div>
      </CabinCrewLayout>
    );
  }
  
  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const question = questions[currentQuestion];
  const isAnswered = answers[question?.id] !== undefined;
  
  return (
    <CabinCrewLayout>
      <div className="min-h-screen py-4 sm:py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="mb-4 sm:mb-6">
            <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="mb-4"
            data-testid="back-to-dashboard-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard'a Dön
          </Button>
          
          <div className="text-center mb-4">
            <Badge className="text-lg px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600">
              {currentLang.flag} {currentLang.name} Seviye Tespit Sınavı
            </Badge>
          </div>
          
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2">
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                Soru {currentQuestion + 1} / {questions.length}
              </span>
              <Badge variant="outline" className="font-bold">
                {question?.level} Seviye
              </Badge>
            </div>
            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
              <Clock className={`w-5 h-5 ${timeRemaining < 300 ? 'text-red-600' : 'text-blue-600'}`} />
              <span className={`font-bold text-lg ${timeRemaining < 300 ? 'text-red-600' : 'text-blue-600'}`}>
                {formatTime(timeRemaining)}
              </span>
            </div>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
        
        <Card className="glass-effect border-none shadow-2xl" data-testid="question-card">
          <CardHeader>
            <CardTitle className="text-2xl">
              {question?.text}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {question?.options.map((option, index) => (
                <button
                  key={index}
                  data-testid={`option-${index}`}
                  onClick={() => handleAnswer(question.id, index)}
                  className={`w-full p-4 text-left rounded-xl transition-all ${
                    answers[question.id] === index
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                      : 'bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{option}</span>
                    {answers[question.id] === index && (
                      <Check className="w-5 h-5" />
                    )}
                  </div>
                </button>
              ))}
            </div>
            
            <div className="flex justify-between mt-8">
              <Button
                data-testid="previous-button"
                variant="outline"
                onClick={() => setCurrentQuestion(Math.max(0, currentQuestion - 1))}
                disabled={currentQuestion === 0}
              >
                Önceki
              </Button>
              
              {currentQuestion === questions.length - 1 ? (
                <Button
                  data-testid="submit-exam-button"
                  onClick={handleSubmit}
                  disabled={!isAnswered || submitting}
                  className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800"
                >
                  {submitting ? 'Gönderiliyor...' : 'Sınavı Bitir'}
                </Button>
              ) : (
                <Button
                  data-testid="next-button"
                  onClick={() => setCurrentQuestion(Math.min(questions.length - 1, currentQuestion + 1))}
                  disabled={!isAnswered}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  Sonraki
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
        
        <div className="mt-6 grid grid-cols-10 gap-2">
          {questions.map((q, idx) => (
            <button
              key={q.id}
              data-testid={`question-nav-${idx}`}
              onClick={() => setCurrentQuestion(idx)}
              className={`p-2 rounded text-sm font-bold transition-all ${
                answers[q.id] !== undefined
                  ? 'bg-green-500 text-white'
                  : idx === currentQuestion
                  ? 'bg-blue-600 text-white'
                  : 'bg-white border-2 border-gray-200 hover:border-blue-300'
              }`}
            >
              {idx + 1}
            </button>
          ))}
        </div>
      </div>
    </div>
    </CabinCrewLayout>
  );
};

export default Exam;
